import React, { useState, useEffect, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { chinaOrderImportService } from '../../../../service/admin/chinaOrderImportService';
import { EditModal, DeleteModal, fetchAllRowsPaged } from '../ActionModals';
import { toast } from 'react-toastify';
import './style.css';
import '../style.css';

const TABLE_COLUMNS = [
  { key: 'chinaLocalTrackingId', label: 'China Local Tracking ID' },
  { key: 'logisticsCarrier',     label: 'Logistics Carrier' },
  { key: 'shipDate',             label: 'Ship Date' },
  { key: 'postageRMB',           label: 'Postage /RMB' },
  { key: 'weightOfParcelKg',     label: 'Weight (kg)' },
  { key: 'invoiceNo',            label: 'Invoice No' },
  { key: 'maskId',               label: 'Mask ID' },
  { key: 'deliveryDate',         label: 'Delivery Date' },
  { key: 'estimatedDOA',         label: 'Estimated DOA' },
  { key: 'arrivedIn',            label: 'Arrived In' },
  { key: 'source',               label: 'Source' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SOURCE_FILTER_OPTIONS = [
  { value: '', label: 'All Source' },
  { value: 'Yeky', label: 'Yeky' },
  { value: 'Sunny', label: 'Sunny' },
  { value: 'custom', label: 'Custom' },
];

// ---- Date helpers ----

/** DD-MM-YYYY → YYYY-MM-DD  (for <input type="date"> value) */
const toInputDate = (str) => {
  if (!str) return '';
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(str);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return '';
};

/** YYYY-MM-DD (from input) → DD-MM-YYYY (for storage / display) */
const fromInputDate = (str) => {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return `${d}-${m}-${y}`;
};

export default function PostageToIndia() {
  const [records, setRecords]         = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [pagination, setPagination]   = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

  // ---- Date filter state (IST) ----
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterSourceCustom, setFilterSourceCustom] = useState('');
  const hasCustomFilter = filterSource === 'custom';
  const hasAnyActiveFilter = Boolean(startDate || endDate || filterSource || filterSourceCustom);

  // Track saving state per row: { [rowId]: true/false }
  const [savingRows, setSavingRows] = useState({});

  // ---- Edit / Delete modal state ----
  const [editRow, setEditRow]           = useState(null);
  const [deleteRow, setDeleteRow]       = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRecords = useCallback(async (page = 1, searchVal = '', limit = pagination.limit, sd = startDate, ed = endDate, src = filterSource, srcCustom = filterSourceCustom) => {
    setIsLoading(true);
    try {
      const resolvedSource = src === 'custom' ? String(srcCustom || '').trim() : src;
      const res = await chinaOrderImportService.getPostageToIndia({ page, limit, search: searchVal, startDate: sd, endDate: ed, source: resolvedSource });
      if (res?.status?.code === 200) {
        setRecords(res.data.records);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        toast.error(res?.error?.message || 'Failed to load records.');
      }
    } catch {
      toast.error('Failed to load Postage to India records.');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit, filterSource, filterSourceCustom]);

  useEffect(() => { fetchRecords(1, '', 20); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    fetchRecords(1, searchInput, pagination.limit, startDate, endDate, filterSource);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    fetchRecords(1, '', pagination.limit, startDate, endDate, filterSource);
  };

  const handleRefresh = () => fetchRecords(pagination.page, search, pagination.limit, startDate, endDate, filterSource);

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchRecords(1, search, newLimit, startDate, endDate, filterSource);
  };

  const handlePageChange = (newPage) => fetchRecords(newPage, search, pagination.limit, startDate, endDate, filterSource);

  const handleDateFilter = () => fetchRecords(1, search, pagination.limit, startDate, endDate, filterSource, filterSourceCustom);

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    setFilterSource('');
    setFilterSourceCustom('');
    fetchRecords(1, search, pagination.limit, '', '', '', '');
  };

  // ---- Edit record ----
  const handleEditSave = async (formData) => {
    setActionLoading(true);
    try {
      const res = await chinaOrderImportService.updatePostageRecord(editRow._id, formData);
      if (res?.status?.code === 200) {
        setRecords(prev => prev.map(r => r._id === editRow._id ? { ...r, ...res.data.record } : r));
        toast.success('Record updated successfully.');
        setEditRow(null);
      } else {
        toast.error(res?.error?.message || 'Failed to update record.');
      }
    } catch { toast.error('Failed to update record.'); }
    finally { setActionLoading(false); }
  };

  // ---- Delete record ----
  const handleDeleteConfirm = async (password) => {
    setActionLoading(true);
    try {
      const res = await chinaOrderImportService.deletePostageRecord(deleteRow._id, password);
      if (res?.status?.code === 200) {
        setRecords(prev => prev.filter(r => r._id !== deleteRow._id));
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
        toast.success('Record deleted successfully.');
        setDeleteRow(null);
      } else {
        toast.error(res?.error?.message || 'Failed to delete record.');
      }
    } catch { toast.error('Failed to delete record.'); }
    finally { setActionLoading(false); }
  };

  const handleDeleteAllConfirm = async (password) => {
    setActionLoading(true);
    try {
      const res = await chinaOrderImportService.deleteAllPostageToIndia(password);
      if (res?.status?.code === 200) {
        const n = res.data?.deletedCount ?? 0;
        toast.success(res.data?.message || `Deleted ${n} record(s).`);
        setRecords([]);
        setPagination((prev) => ({ ...prev, total: 0, totalPages: 1, page: 1 }));
        setShowDeleteAllModal(false);
      } else {
        toast.error(res?.error?.message || 'Failed to delete all records.');
      }
    } catch {
      toast.error('Failed to delete all records.');
    } finally {
      setActionLoading(false);
    }
  };

  // ---- Delivery date inline save ----
  const handleDeliveryDateSave = async (rowId, inputValue) => {
    const deliveryDate = fromInputDate(inputValue); // DD-MM-YYYY
    if (!deliveryDate) return;

    setSavingRows(prev => ({ ...prev, [rowId]: true }));
    try {
      const res = await chinaOrderImportService.updatePostageRecord(rowId, { deliveryDate });
      if (res?.status?.code === 200) {
        const updated = res.data.record;
        // Update just this row in local state
        setRecords(prev => prev.map(r => r._id === rowId ? { ...r, deliveryDate: updated.deliveryDate, arrivedIn: updated.arrivedIn } : r));
        toast.success('Delivery date saved.');
      } else {
        toast.error(res?.error?.message || 'Failed to save delivery date.');
      }
    } catch {
      toast.error('Failed to save delivery date.');
    } finally {
      setSavingRows(prev => ({ ...prev, [rowId]: false }));
    }
  };

  // ---- Export — 4 cols only, paged fetch + progress ----
  const [isExporting, setIsExporting] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [exportProgressPercent, setExportProgressPercent] = useState(0);
  const [exportProgressLabel, setExportProgressLabel] = useState('');

  const EXPORT_PAGE_SIZE = 5000;
  const FETCH_PHASE_MAX_PCT = 88;

  const EXPORT_COLS = [
    { header: 'Invoice No',              key: 'invoiceNo' },
    { header: 'China Local Tracking ID', key: 'chinaLocalTrackingId' },
    { header: 'Logistics Carrier',       key: 'logisticsCarrier' },
    { header: 'Mask ID',                 key: 'maskId' },
    { header: 'Delivery Date',           key: 'deliveryDate' },
  ];

  const handleExport = async () => {
    setIsExporting(true);
    setShowExportProgressModal(true);
    setExportProgressPercent(2);
    setExportProgressLabel('Starting export…');
    try {
      const { rows, total } = await fetchAllRowsPaged({
        fetchPage: (params) => chinaOrderImportService.getPostageToIndia(params),
        pageSize: EXPORT_PAGE_SIZE,
        extraQuery: {
          search,
          startDate,
          endDate,
          source: filterSource === 'custom' ? String(filterSourceCustom || '').trim() : filterSource,
        },
        onProgress: ({ loaded, total: t, page, totalPages }) => {
          const denom = Math.max(1, t);
          const pct = Math.min(
            FETCH_PHASE_MAX_PCT,
            Math.max(5, Math.round((loaded / denom) * FETCH_PHASE_MAX_PCT))
          );
          setExportProgressPercent(pct);
          setExportProgressLabel(
            `Loaded ${loaded} of ${t} row(s) — page ${page} / ${totalPages}`
          );
        },
      });

      if (total === 0 || rows.length === 0) {
        toast.warn('No data to export.');
        setShowExportProgressModal(false);
        return;
      }

      setExportProgressLabel('Building Excel file…');
      setExportProgressPercent(92);

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Postage to India');
      sheet.addRow(EXPORT_COLS.map((c) => c.header));
      sheet.getRow(1).font = { bold: true };
      rows.forEach((row) => {
        sheet.addRow(EXPORT_COLS.map((c) => row[c.key] ?? ''));
      });

      EXPORT_COLS.forEach((col, idx) => {
        const colNum = idx + 1;
        let maxLen = col.header.length;
        sheet.getColumn(colNum).eachCell({ includeEmpty: false }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        sheet.getColumn(colNum).width = Math.min(Math.max(maxLen + 2, 12), 50);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'PostageToIndia_Export.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      setExportProgressPercent(100);
      setExportProgressLabel('Export complete');
      toast.success(`Exported ${rows.length} record(s).`);
      setTimeout(() => setShowExportProgressModal(false), 500);
    } catch (e) {
      toast.error(e?.message || 'Export failed.');
      setShowExportProgressModal(false);
    } finally {
      setIsExporting(false);
    }
  };

  const formatVal = (val) => (val === null || val === undefined || val === '' ? '-' : String(val));

  return (
    <div className="pi-container">
      <div className="pi-header">
        <h1>Postage to India</h1>
      </div>

      {/* Toolbar */}
      <div className="cu-toolbar cu-toolbar-wrap cu-toolbar-compact">
        <form className="cu-search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            className="cu-search-input"
            placeholder="Search by Tracking ID, Carrier, Invoice No, Mask ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button type="button" className="cu-clear-btn" onClick={handleClearSearch}>&times;</button>
          )}
          <button type="submit" className="cu-btn cu-btn-search">Search</button>
        </form>

        {/* Source filter */}
        <div className="cu-date-filter">
          <select
            className="cu-select"
            value={filterSource}
            onChange={(e) => {
              const next = e.target.value;
              setFilterSource(next);
              if (next !== 'custom') setFilterSourceCustom('');
              fetchRecords(1, search, pagination.limit, startDate, endDate, next, '');
            }}
          >
            {SOURCE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-source'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {filterSource === 'custom' && (
            <input
              type="text"
              className="cu-date-input"
              placeholder="Source"
              value={filterSourceCustom}
              onChange={(e) => setFilterSourceCustom(e.target.value)}
            />
          )}
        </div>

        {/* Date range filter — Ship Date */}
        <div className="cu-date-filter">
          <label className="cu-label">Ship Date From</label>
          <input
            type="date"
            className="cu-date-input"
            value={startDate}
            onChange={(e) => {
              const next = e.target.value;
              setStartDate(next);
              fetchRecords(1, search, pagination.limit, next, endDate, filterSource, filterSourceCustom);
            }}
          />
          <label className="cu-label">To</label>
          <input
            type="date"
            className="cu-date-input"
            value={endDate}
            onChange={(e) => {
              const next = e.target.value;
              setEndDate(next);
              fetchRecords(1, search, pagination.limit, startDate, next, filterSource, filterSourceCustom);
            }}
          />
          {hasCustomFilter && (
            <button type="button" className="cu-btn cu-btn-search cu-btn-apply" onClick={handleDateFilter} style={{borderRadius:'var(--cu-radius)'}}>Apply</button>
          )}
          {hasAnyActiveFilter && (
            <button type="button" className="cu-btn cu-btn-refresh" onClick={handleClearDates}>✕ Clear</button>
          )}
        </div>

        <div className="cu-toolbar-right">
          <label className="cu-label">Show</label>
          <select className="cu-select" value={pagination.limit} onChange={handleLimitChange}>
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <label className="cu-label">per page</label>
          <span className="cu-total-badge">{pagination.total} total</span>
          <button type="button" className="cu-btn cu-btn-refresh" onClick={handleRefresh}>
            ↻ Refresh
          </button>
          <button type="button" className="cu-btn cu-btn-export" onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            className="cu-btn cu-btn-delete-all"
            disabled={isLoading || pagination.total < 1}
            onClick={() => {
              setDeleteRow(null);
              setShowDeleteAllModal(true);
            }}
          >
            Delete all
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="pi-table-wrapper">
        {isLoading ? (
          <div className="cu-loading">Loading...</div>
        ) : (
          <table className="pi-table">
            <thead>
              <tr>
                <th className="pi-th pi-th-sno">#</th>
                {TABLE_COLUMNS.map(c => <th key={c.key} className="pi-th">{c.label}</th>)}
                <th className="pi-th pi-th-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((row, i) => (
                  <tr key={row._id || i} className="pi-tr">
                    <td className="pi-td pi-td-sno">{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    {TABLE_COLUMNS.map(c => (
                      <td key={c.key} className="pi-td">
                        {c.key === 'deliveryDate' ? (
                          <DeliveryDateCell rowId={row._id} value={row.deliveryDate} saving={!!savingRows[row._id]} onSave={handleDeliveryDateSave} />
                        ) : c.key === 'arrivedIn' ? (
                          row.arrivedIn ? <span className="pi-arrived-badge">{row.arrivedIn}</span> : <span className="pi-td-empty">-</span>
                        ) : c.key === 'source' ? (
                          <span className={`pi-badge ${(row.source || '').toLowerCase()}`}>{row.source || '-'}</span>
                        ) : formatVal(row[c.key])}
                      </td>
                    ))}
                    <td className="pi-td">
                      <div className="cu-action-cell">
                        <button className="cu-action-btn cu-action-btn-edit"   onClick={() => setEditRow(row)}>Edit</button>
                        <button className="cu-action-btn cu-action-btn-delete" onClick={() => setDeleteRow(row)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length + 1} className="pi-empty">
                    {search ? `No records found for "${search}".` : 'No Postage to India records yet. Import an Excel file from the Order Details page.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="cu-pagination">
          <button className="cu-page-btn" disabled={pagination.page <= 1} onClick={() => handlePageChange(1)}>«</button>
          <button className="cu-page-btn" disabled={pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>‹ Prev</button>
          <span className="cu-page-info">Page {pagination.page} of {pagination.totalPages}</span>
          <button className="cu-page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>Next ›</button>
          <button className="cu-page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.totalPages)}>»</button>
        </div>
      )}

      {/* ---- Edit Modal ---- */}
      {editRow && (
        <EditModal
          title="Edit Postage to India"
          fields={[
            { key: 'chinaLocalTrackingId', label: 'China Local Tracking ID' },
            { key: 'logisticsCarrier',     label: 'Logistics Carrier' },
            { key: 'shipDate',             label: 'Ship Date',     type: 'date' },
            { key: 'postageRMB',           label: 'Postage /RMB' },
            { key: 'weightOfParcelKg',     label: 'Weight (kg)' },
            { key: 'invoiceNo',            label: 'Invoice No', readOnly: true },
            { key: 'maskId',               label: 'Mask ID' },
            { key: 'deliveryDate',         label: 'Delivery Date', type: 'date' },
            { key: 'estimatedDOA',         label: 'Estimated DOA', readOnly: true },
            { key: 'arrivedIn',            label: 'Arrived In',    readOnly: true },
            { key: 'source',               label: 'Source' },
          ]}
          initialData={editRow}
          onSave={handleEditSave}
          onClose={() => setEditRow(null)}
          loading={actionLoading}
        />
      )}

      {/* ---- Delete Modal ---- */}
      {deleteRow && (
        <DeleteModal
          title={`Delete — ${deleteRow.invoiceNo || deleteRow.chinaLocalTrackingId || 'record'}`}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteRow(null)}
          loading={actionLoading}
        />
      )}

      {showDeleteAllModal && (
        <DeleteModal
          title="Delete all Postage to India"
          description={(
            <>
              Every row in <strong>Postage to India</strong> will be removed from the database. This cannot be undone.
              <br />
              Enter your admin password to confirm.
            </>
          )}
          confirmButtonText="Delete all"
          onConfirm={handleDeleteAllConfirm}
          onClose={() => setShowDeleteAllModal(false)}
          loading={actionLoading}
        />
      )}

      {showExportProgressModal && (
        <div className="cu-export-progress-overlay">
          <div className="cu-export-progress-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cu-export-progress-header">
              <h2>Export Progress</h2>
            </div>
            <div className="cu-export-progress-body">
              <p className="cu-export-progress-label">{exportProgressLabel}</p>
              <div className="cu-export-progress-track">
                <div className="cu-export-progress-fill" style={{ width: `${exportProgressPercent}%` }} />
              </div>
              <div className="cu-export-progress-pct">{exportProgressPercent}%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Delivery Date inline-edit cell ----
function DeliveryDateCell({ rowId, value, saving, onSave }) {
  const [inputVal, setInputVal] = useState(toInputDate(value));
  const [dirty, setDirty]       = useState(false);

  // Sync if parent value changes (e.g. after refresh)
  useEffect(() => {
    setInputVal(toInputDate(value));
    setDirty(false);
  }, [value]);

  const handleChange = (e) => {
    setInputVal(e.target.value);
    setDirty(true);
  };

  const handleSave = () => {
    if (!dirty || !inputVal) return;
    onSave(rowId, inputVal);
    setDirty(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    if (e.key === 'Escape') { setInputVal(toInputDate(value)); setDirty(false); }
  };

  return (
    <div className="pi-delivery-cell">
      <input
        type="date"
        className={`pi-delivery-input ${dirty ? 'pi-delivery-dirty' : ''}`}
        value={inputVal}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={saving}
        title="Click to set delivery date"
      />
      {/* Save button — visible only when date has been changed */}
      {dirty && !saving && (
        <button
          type="button"
          className="pi-save-btn"
          onClick={handleSave}
          title="Save delivery date"
        >
          Save
        </button>
      )}
      {saving && <span className="pi-saving-dot" title="Saving…">●</span>}
    </div>
  );
}
